import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Plus, Trash2 } from 'lucide-react';

import { useApp } from '@/presentation/hooks/use-app.js';

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

type Props = {
    domains: string[];
    onChange: (domains: string[]) => void;
    readOnly?: boolean;
};

export function isValidEmailDomain(domain: string): boolean {
    if (!domain.trim()) {
        return true;
    }
    return DOMAIN_REGEX.test(domain.trim().toLowerCase());
}

export function EmailDomainsSection({ domains, onChange, readOnly = false }: Props) {
    const { translate } = useApp();
    const list = domains.length > 0 ? domains : [''];

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
            <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {translate('clients.emailDomains.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('clients.emailDomains.description')}
                </Typography>
                <Stack spacing={1}>
                    {list.map((domain, index) => {
                        const invalid = domain.trim().length > 0 && !isValidEmailDomain(domain);
                        return (
                            <Stack key={`domain-${index}`} direction="row" spacing={1} alignItems="flex-start">
                                <TextField
                                    value={domain}
                                    onChange={(event) => {
                                        const value = event.target.value.toLowerCase();
                                        const next = [...list];
                                        next[index] = value;
                                        onChange(next);
                                    }}
                                    fullWidth
                                    size="small"
                                    placeholder={translate('clients.emailDomains.placeholder')}
                                    error={invalid}
                                    helperText={invalid ? translate('clients.emailDomains.invalid') : undefined}
                                    disabled={readOnly}
                                />
                                {!readOnly && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={list.length <= 1}
                                        onClick={() => {
                                            const next = list.filter((_, i) => i !== index);
                                            onChange(next.length > 0 ? next : ['']);
                                        }}
                                        title={translate('clients.emailDomains.remove')}
                                        sx={{ mt: 0.5 }}
                                    >
                                        <Trash2 size={16} />
                                    </IconButton>
                                )}
                            </Stack>
                        );
                    })}
                    {!readOnly && (
                        <Box>
                            <Button size="small" startIcon={<Plus size={16} />} onClick={() => onChange([...list, ''])} sx={{ textTransform: 'none' }}>
                                {translate('clients.emailDomains.add')}
                            </Button>
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
